import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Settings, 
  Zap, 
  User, 
  Shield, 
  DollarSign, 
  Clock,
  CheckCircle,
  XCircle,
  Edit,
  Eye,
  Play,
  ArrowLeft,
  Key,
  Users,
  Activity,
  Save,
  Trash2,
  RotateCw
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import ModelProviderBadge from './ModelProviderBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyConfiguration = ({ company, onBack }) => {
  const [activeTab, setActiveTab] = useState('modules');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showAgentDetails, setShowAgentDetails] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [showAgent00Config, setShowAgent00Config] = useState(null); // Module type for Agent 00 config
  const [agent00Prompt, setAgent00Prompt] = useState('');
  const [showClearDataConfirm, setShowClearDataConfirm] = useState(false);
  const [clearDataLoading, setClearDataLoading] = useState(false);
  const [companyName, setCompanyName] = useState(company.name);
  const [editCompanyData, setEditCompanyData] = useState({
    razon_social: company.razon_social || '',
    nombre_comercial: company.nombre_comercial || '',
    ceo_director: company.ceo_director || '',
    email_principal: company.email_principal || '',
    telefono: company.telefono || '',
    industria: company.industria || '',
    pais: company.pais || '',
    ciudad: company.ciudad || '',
    empleados: company.empleados || null
  });
  const [newUser, setNewUser] = useState({ 
    email: '', 
    password: '', 
    full_name: '', 
    role: 'user' 
  });
  const queryClient = useQueryClient();

  // Queries
  const { data: modules = [] } = useQuery({
    queryKey: ['company-modules', company.id],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies/${company.id}/modules`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true
  });

  const { data: agents = [] } = useQuery({
    queryKey: ['company-agents', company.id],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies/${company.id}/agents`);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true
  });

  const { data: executions = [] } = useQuery({
    queryKey: ['company-executions', company.id],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies/${company.id}/executions?limit=20`);
      return response.data;
    }
  });

  const { data: users = [] } = useQuery({
    queryKey: ['company-users', company.id],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies/${company.id}/users`);
      return response.data;
    }
  });

  // Mutations
  const updateModuleMutation = useMutation({
    mutationFn: async ({ moduleType, isEnabled, modelOverride, customName, temperature, maxTokens }) => {
      const response = await axios.put(`${API}/companies/${company.id}/modules`, {
        module_type: moduleType,
        is_enabled: isEnabled,
        model_override: modelOverride,
        custom_name: customName,
        temperature: temperature,
        max_tokens: maxTokens
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['company-modules', company.id]);
      setEditingModule(null);
      toast.success('Módulo actualizado');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al actualizar módulo');
    }
  });

  const toggleAgentMutation = useMutation({
    mutationFn: async ({ agentId, isEnabled }) => {
      const response = await axios.put(`${API}/companies/${company.id}/agents/${agentId}`, {
        is_enabled: isEnabled
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['company-agents', company.id]);
      toast.success('Agente actualizado');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al actualizar agente');
    }
  });

  // Agent 00 Config Query
  const { data: agent00Config, refetch: refetchAgent00Config } = useQuery({
    queryKey: ['agent00Config', company.id, showAgent00Config],
    queryFn: async () => {
      if (!showAgent00Config) return null;
      const response = await axios.get(
        `${API}/companies/${company.id}/modules/${showAgent00Config}/agent00/config`
      );
      return response.data;
    },
    enabled: !!showAgent00Config
  });

  // Load Agent 00 prompt when modal opens
  React.useEffect(() => {
    if (agent00Config) {
      setAgent00Prompt(agent00Config.custom_prompt || '');
    }
  }, [agent00Config]);

  // Agent 00 Config Mutation
  const saveAgent00ConfigMutation = useMutation({
    mutationFn: async ({ moduleType, customPrompt }) => {
      const response = await axios.post(
        `${API}/companies/${company.id}/modules/${moduleType}/agent00/config`,
        { custom_prompt: customPrompt }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Configuración del Agent 00 guardada exitosamente');
      setShowAgent00Config(null);
      setAgent00Prompt('');
      refetchAgent00Config();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    }
  });

  const createUserMutation = useMutation({
    mutationFn: async (userData) => {
      const response = await axios.post(`${API}/auth/register`, {
        ...userData,
        company_id: company.id
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['company-users', company.id]);
      setShowCreateUser(false);
      setNewUser({ email: '', password: '', full_name: '', role: 'user' });
      toast.success('Usuario creado exitosamente');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  });


  const deleteUserMutation = useMutation({
    mutationFn: async (userId) => {
      const response = await axios.delete(`${API}/companies/${company.id}/users/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['company-users', company.id]);
      toast.success('Usuario eliminado exitosamente');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (userId) => {
      const response = await axios.post(`${API}/companies/${company.id}/users/${userId}/reset-password`);
      return response.data;
    },
    onSuccess: (data) => {
      // Show success message with temporary password
      const message = `Contraseña reseteada exitosamente\n\nEmail: ${data.email}\nContraseña temporal: ${data.temporary_password}\n\nEl usuario debe cambiar esta contraseña en su primer inicio de sesión.`;
      
      // Copy to clipboard
      navigator.clipboard.writeText(data.temporary_password).catch(() => {});
      
      toast.success(message, { duration: 10000 });
      
      // Also show an alert for emphasis
      setTimeout(() => {
        alert(`✅ Contraseña reseteada exitosamente\n\n📧 Email: ${data.email}\n🔑 Contraseña temporal: ${data.temporary_password}\n\n✨ La contraseña se copió al portapapeles`);
      }, 100);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al resetear contraseña');
    }
  });

  const handleDeleteUser = (user) => {
    if (window.confirm(`¿Estás seguro de que deseas eliminar al usuario ${user.full_name} (${user.email})?\n\nEsta acción no se puede deshacer.`)) {
      deleteUserMutation.mutate(user.id);
    }
  };

  const handleResetPassword = (user) => {
    if (window.confirm(`¿Deseas resetear la contraseña de ${user.full_name}?\n\nSe generará una contraseña temporal.`)) {
      resetPasswordMutation.mutate(user.id);
    }
  };


  const updateCompanyProfileMutation = useMutation({
    mutationFn: async (profileData) => {
      const response = await axios.put(`${API}/companies/${company.id}/profile`, profileData);
      return response.data;
    },
    onSuccess: (updatedCompany) => {
      setShowEditCompany(false);
      toast.success('Perfil de empresa actualizado exitosamente');
      
      // Update local state with new data
      setEditCompanyData({
        razon_social: updatedCompany.razon_social || '',
        nombre_comercial: updatedCompany.nombre_comercial || '',
        ceo_director: updatedCompany.ceo_director || '',
        email_principal: updatedCompany.email_principal || '',
        telefono: updatedCompany.telefono || '',
        industria: updatedCompany.industria || '',
        pais: updatedCompany.pais || '',
        ciudad: updatedCompany.ciudad || '',
        empleados: updatedCompany.empleados || null
      });
      
      // Update company name display
      if (updatedCompany.nombre_comercial) {
        setCompanyName(updatedCompany.nombre_comercial);
      } else if (updatedCompany.name) {
        setCompanyName(updatedCompany.name);
      }
      
      // Update parent company object
      Object.assign(company, updatedCompany);
      
      // Refresh company list in parent
      queryClient.invalidateQueries(['companies']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    }
  });

  const handleModuleToggle = (module, isEnabled) => {
    updateModuleMutation.mutate({
      moduleType: module.type,
      isEnabled,
      modelOverride: module.model_override,
      customName: module.custom_name,
      temperature: module.temperature,
      maxTokens: module.max_tokens
    });
  };

  const handleModelChange = (module, modelOverride) => {
    updateModuleMutation.mutate({
      moduleType: module.type,
      isEnabled: module.is_enabled,
      modelOverride,
      customName: module.custom_name,
      temperature: module.temperature,
      maxTokens: module.max_tokens
    });
  };

  const handleModuleConfigSave = () => {
    if (editingModule) {
      updateModuleMutation.mutate({
        moduleType: editingModule.type,
        isEnabled: editingModule.is_enabled,
        modelOverride: editingModule.model_override,
        customName: editingModule.custom_name,
        temperature: editingModule.temperature,
        maxTokens: editingModule.max_tokens
      });
    }
  };

  const handleAgentToggle = (agent, isEnabled) => {
    toggleAgentMutation.mutate({
      agentId: agent.id,
      isEnabled
    });
  };

  const handleCreateUser = () => {
    if (!newUser.email || !newUser.password || !newUser.full_name) {
      toast.error('Complete todos los campos');
      return;
    }
    createUserMutation.mutate(newUser);
  };

  const handleUpdateCompanyProfile = () => {
    // Clean data before sending - convert empty strings to null for numeric fields
    const cleanData = { ...editCompanyData };
    if (cleanData.empleados === '' || cleanData.empleados === null) {
      delete cleanData.empleados; // Don't send if empty
    } else {
      cleanData.empleados = parseInt(cleanData.empleados); // Ensure it's a number
    }
    
    // Remove empty string fields
    Object.keys(cleanData).forEach(key => {
      if (cleanData[key] === '') {
        cleanData[key] = null;
      }
    });
    
    updateCompanyProfileMutation.mutate(cleanData);
  };

  const enabledModulesCount = modules.filter(m => m.is_enabled).length;
  const enabledAgentsCount = agents.filter(a => a.is_enabled).length;
  const recentExecutionsCount = executions.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={onBack}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div className="h-6 w-px bg-slate-600" />
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">{companyName.charAt(0)}</span>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">{companyName}</h1>
                <p className="text-slate-400">{company.domain}</p>
              </div>
            </div>
          </div>
          <Button
            onClick={() => setShowEditCompany(true)}
            className="btn-gradient"
          >
            <Edit className="w-4 h-4 mr-2" />
            Editar Perfil
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="glass-dark border-blue-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Módulos Habilitados</p>
                  <p className="text-2xl font-bold text-white">{enabledModulesCount}</p>
                </div>
                <Zap className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="glass-dark border-green-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Agentes Activos</p>
                  <p className="text-2xl font-bold text-white">{enabledAgentsCount}</p>
                </div>
                <Activity className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="glass-dark border-purple-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Ejecuciones Recientes</p>
                  <p className="text-2xl font-bold text-white">{recentExecutionsCount}</p>
                </div>
                <Clock className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="glass-dark border-cyan-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Estado</p>
                  <p className="text-lg font-bold text-green-400">Activa</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Configuration Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 border-slate-600">
          <TabsTrigger value="modules" className="data-[state=active]:bg-blue-600">
            <Zap className="w-4 h-4 mr-2" />
            Módulos
          </TabsTrigger>
          <TabsTrigger value="agents" className="data-[state=active]:bg-blue-600">
            <Activity className="w-4 h-4 mr-2" />
            Agentes
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-blue-600">
            <Users className="w-4 h-4 mr-2" />
            Usuarios
          </TabsTrigger>
          <TabsTrigger value="executions" className="data-[state=active]:bg-blue-600">
            <Play className="w-4 h-4 mr-2" />
            Ejecuciones
          </TabsTrigger>
          <TabsTrigger value="data" className="data-[state=active]:bg-red-600">
            <Trash2 className="w-4 h-4 mr-2" />
            Datos
          </TabsTrigger>
        </TabsList>

        {/* Modules Configuration */}
        <TabsContent value="modules" className="space-y-6">
          <Card className="glass-dark border-blue-500/20">
            <CardHeader>
              <CardTitle className="text-white">Configuración de Módulos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {modules.map((module) => (
                  <div key={module.id} className="p-6 rounded-lg bg-slate-800/50 border border-slate-700">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-white mb-1">{module.name}</h3>
                        <p className="text-sm text-slate-400">{module.description}</p>
                      </div>
                      <Switch
                        checked={module.is_enabled}
                        onCheckedChange={(checked) => handleModuleToggle(module, checked)}
                      />
                    </div>

                    {module.is_enabled && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Modelo AI Override
                          </label>
                          <Select
                            value={module.model_override || module.default_model}
                            onValueChange={(value) => handleModelChange(module, value)}
                          >
                            <SelectTrigger className="bg-slate-700 border-slate-600">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <optgroup label="🤖 OpenAI">
                                <SelectItem value="gpt-5">GPT-5 (Más reciente)</SelectItem>
                                <SelectItem value="gpt-4o">GPT-4o (Balanceado)</SelectItem>
                                <SelectItem value="gpt-4o-mini">GPT-4o Mini (Rápido)</SelectItem>
                                <SelectItem value="gpt-4.1">GPT-4.1 (Precisión)</SelectItem>
                              </optgroup>
                              <optgroup label="🧠 Anthropic Claude 4">
                                <SelectItem value="claude-opus-4.1">Claude Opus 4.1 (Máxima capacidad)</SelectItem>
                                <SelectItem value="claude-sonnet-4.5">Claude Sonnet 4.5 (Balanceado)</SelectItem>
                                <SelectItem value="claude-haiku-4.5">Claude Haiku 4.5 (Rápido)</SelectItem>
                              </optgroup>
                              <optgroup label="🧠 Anthropic Claude 3">
                                <SelectItem value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (Superior)</SelectItem>
                                <SelectItem value="claude-3-haiku-20240307">Claude 3 Haiku (Rápido)</SelectItem>
                                <SelectItem value="claude-3-opus-20240229">Claude 3 Opus (Máximo)</SelectItem>
                              </optgroup>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex justify-between items-center text-xs text-slate-400">
                          <span>Tipo: {module.type.replace('_', ' ').toUpperCase()}</span>
                          <div className="flex gap-2">
                            <ModelProviderBadge model={module.model_override || module.default_model} />
                            <Badge className="badge-success">CONFIGURADO</Badge>
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-slate-700">
                          <Button
                            onClick={() => setShowAgent00Config(module.module_type)}
                            size="sm"
                            variant="outline"
                            className="w-full text-blue-400 border-blue-500/30 hover:bg-blue-500/10"
                          >
                            <Settings className="w-4 h-4 mr-2" />
                            Configurar Agente 00
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agents Configuration */}
        <TabsContent value="agents" className="space-y-6">
          <Card className="glass-dark border-blue-500/20">
            <CardHeader>
              <CardTitle className="text-white">Agentes Disponibles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {agents.map((agent) => (
                  <div key={agent.id} className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-white text-sm">{agent.name}</h4>
                      <Switch
                        checked={agent.is_enabled}
                        onCheckedChange={(checked) => handleAgentToggle(agent, checked)}
                      />
                    </div>
                    
                    <p className="text-xs text-slate-400 mb-3 line-clamp-2">{agent.description}</p>
                    
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>Uso: {agent.usage_count || 0}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowAgentDetails(agent)}
                        className="h-6 px-2 text-blue-400 hover:text-blue-300"
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                    </div>
                    
                    <div className="mt-2">
                      <Badge 
                        className={`text-xs ${
                          agent.module_type === 'revenue' ? 'badge-success' :
                          agent.module_type === 'supply_chain' ? 'badge-warning' :
                          agent.module_type === 'operaciones' ? 'badge-info' :
                          'badge-error'
                        }`}
                      >
                        {agent.module_type.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Management */}
        <TabsContent value="users" className="space-y-6">
          <Card className="glass-dark border-blue-500/20">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-white">Gestión de Usuarios</CardTitle>
                <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                  <DialogTrigger asChild>
                    <Button className="btn-gradient">
                      <User className="w-4 h-4 mr-2" />
                      Crear Usuario
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass-dark border-blue-500/20 text-white">
                    <DialogHeader>
                      <DialogTitle>Crear Usuario para {company.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Nombre Completo
                        </label>
                        <Input
                          value={newUser.full_name}
                          onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                          className="form-input"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Email
                        </label>
                        <Input
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          className="form-input"
                          placeholder="john@acme.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Contraseña Temporal
                        </label>
                        <Input
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          className="form-input"
                          placeholder="••••••••"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Rol
                        </label>
                        <Select
                          value={newUser.role}
                          onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                        >
                          <SelectTrigger className="form-input">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin (Ingesta + Inteligencia Ejecutiva + Ruta Estratégica)</SelectItem>
                            <SelectItem value="staff">Staff (Solo Ingesta)</SelectItem>
                            <SelectItem value="user">Usuario (Legacy)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleCreateUser}
                        className="w-full btn-gradient"
                        disabled={createUserMutation.isPending}
                      >
                        {createUserMutation.isPending ? 'Creando...' : 'Crear Usuario'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <div className="bg-slate-800/50 p-6 rounded-lg text-center">
                  <Users className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <h3 className="text-lg font-medium text-slate-300 mb-2">No hay usuarios</h3>
                  <p className="text-slate-400 mb-4">
                    Crea el primer usuario para {company.name}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {user.full_name?.charAt(0) || user.email?.charAt(0) || 'U'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-white">{user.full_name}</p>
                          <p className="text-sm text-slate-400">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={
                          user.role === 'admin' ? 'badge-info' : 
                          user.role === 'staff' ? 'badge-warning' : 
                          user.role === 'user' ? 'badge-success' : 
                          'badge-secondary'
                        }>
                          {user.role === 'admin' ? 'Admin' : 
                           user.role === 'staff' ? 'Staff' : 
                           user.role === 'user' ? 'Usuario' : 
                           user.role}
                        </Badge>
                        <Badge className={user.is_active ? 'badge-success' : 'badge-error'}>
                          {user.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                        
                        {/* Action buttons */}
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleResetPassword(user)}
                            size="sm"
                            variant="outline"
                            className="h-8"
                            disabled={resetPasswordMutation.isPending}
                          >
                            <RotateCw className="w-3 h-3" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteUser(user)}
                            size="sm"
                            variant="destructive"
                            className="h-8"
                            disabled={deleteUserMutation.isPending}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="bg-slate-800/50 p-6 rounded-lg text-center mt-6">
                <Key className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <h3 className="text-lg font-medium text-slate-300 mb-2">Credenciales de Acceso</h3>
                <p className="text-slate-400 mb-4">
                  Los usuarios de esta empresa pueden acceder con sus credenciales en:
                </p>
                <div className="bg-slate-700 p-3 rounded-lg font-mono text-sm text-blue-400 mb-4">
                  {window.location.origin}
                </div>
                <p className="text-xs text-slate-500">
                  Cada usuario recibirá acceso solo a los módulos habilitados para {company.name}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Executions Log */}
        <TabsContent value="executions" className="space-y-6">
          <Card className="glass-dark border-blue-500/20">
            <CardHeader>
              <CardTitle className="text-white">Registro de Ejecuciones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {executions.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    No hay ejecuciones registradas aún
                  </div>
                ) : (
                  executions.map((execution) => (
                    <div key={execution.id} className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{execution.agent_name}</span>
                          <Badge className={
                            execution.status === 'completed' ? 'badge-success' :
                            execution.status === 'error' ? 'badge-error' :
                            execution.status === 'running' ? 'badge-warning' :
                            'badge-info'
                          }>
                            {execution.status}
                          </Badge>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(execution.created_at).toLocaleString()}
                        </span>
                      </div>
                      {execution.execution_time_ms && (
                        <div className="text-xs text-slate-400">
                          Tiempo de ejecución: {execution.execution_time_ms}ms
                        </div>
                      )}
                      {execution.error_message && (
                        <div className="text-xs text-red-400 mt-1">
                          Error: {execution.error_message}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Management */}
        <TabsContent value="data" className="space-y-6">
          <Card className="glass-dark border-red-500/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-400" />
                Gestión de Datos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Clear All Analysis Data */}
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                <h3 className="text-lg font-semibold text-red-400 mb-2">
                  Limpiar Todos los Datos de Análisis
                </h3>
                <p className="text-sm text-slate-400 mb-4">
                  Esta acción eliminará permanentemente:
                </p>
                <ul className="text-sm text-slate-400 mb-4 list-disc list-inside space-y-1">
                  <li>Todos los <strong className="text-white">Insights</strong> generados</li>
                  <li>Todas las <strong className="text-white">Rutas Estratégicas</strong></li>
                  <li>Todos los <strong className="text-white">KPIs</strong> del Mission Control</li>
                  <li>Todo el <strong className="text-white">Historial de Conversaciones</strong></li>
                  <li>Todas las <strong className="text-white">Ejecuciones de Agentes</strong></li>
                </ul>
                <p className="text-xs text-red-400 mb-4">
                  ⚠️ Esta acción no se puede deshacer. Los datos de configuración (módulos, agentes, usuarios) NO serán afectados.
                </p>
                <Button
                  onClick={() => setShowClearDataConfirm(true)}
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Limpiar Todos los Datos de Análisis
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirm Clear Data Dialog */}
      <Dialog open={showClearDataConfirm} onOpenChange={setShowClearDataConfirm}>
        <DialogContent className="glass-dark border-red-500/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Confirmar Eliminación
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300 mb-4">
              ¿Estás seguro de que deseas eliminar <strong>TODOS</strong> los datos de análisis de esta empresa?
            </p>
            <p className="text-sm text-red-400 mb-4">
              Esta acción eliminará permanentemente todos los insights, rutas estratégicas, KPIs y conversaciones.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={async () => {
                  setClearDataLoading(true);
                  try {
                    const response = await axios.delete(`${API}/companies/${company.id}/clear-all-analysis`);
                    const deleted = response.data.deleted;
                    toast.success(
                      `Datos eliminados: ${deleted.insights} insights, ${deleted.threads} conversaciones, ${deleted.kpis} KPIs, ${deleted.executions} ejecuciones`
                    );
                    setShowClearDataConfirm(false);
                    // Invalidate relevant queries
                    queryClient.invalidateQueries(['company-executions']);
                    queryClient.invalidateQueries(['insights']);
                    queryClient.invalidateQueries(['threads']);
                    queryClient.invalidateQueries(['kpi-comparisons']);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Error al limpiar datos');
                  } finally {
                    setClearDataLoading(false);
                  }
                }}
                disabled={clearDataLoading}
                variant="destructive"
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {clearDataLoading ? (
                  <>
                    <RotateCw className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Sí, Eliminar Todo
                  </>
                )}
              </Button>
              <Button
                onClick={() => setShowClearDataConfirm(false)}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300"
                disabled={clearDataLoading}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent Details Dialog */}
      <Dialog open={!!showAgentDetails} onOpenChange={() => setShowAgentDetails(null)}>
        <DialogContent className="glass-dark border-blue-500/20 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Configuración del Agente: {showAgentDetails?.name}</DialogTitle>
          </DialogHeader>
          {showAgentDetails && (
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-slate-200 mb-2">Descripción</h4>
                <p className="text-sm text-slate-400">{showAgentDetails.description}</p>
              </div>
              
              <div>
                <h4 className="font-medium text-slate-200 mb-2">Schema de Entrada (JSON)</h4>
                <Textarea
                  value={JSON.stringify(showAgentDetails.input_schema, null, 2)}
                  readOnly
                  className="font-mono text-xs bg-slate-800 border-slate-600 h-32"
                />
              </div>
              
              <div>
                <h4 className="font-medium text-slate-200 mb-2">Prompt del Agente</h4>
                <Textarea
                  value={showAgentDetails.agent_prompt}
                  readOnly
                  className="bg-slate-800 border-slate-600 h-24 text-sm"
                />
              </div>
              
              <div className="flex justify-between text-sm text-slate-400">
                <span>Módulo: {showAgentDetails.module_type}</span>
                <span>Uso total: {showAgentDetails.usage_count || 0} ejecuciones</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Company Profile Dialog */}
      <Dialog open={showEditCompany} onOpenChange={setShowEditCompany}>
        <DialogContent className="sm:max-w-[700px] bg-slate-800 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gradient">
              Editar Perfil de Empresa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Razón Social
                </label>
                <Input
                  value={editCompanyData.razon_social}
                  onChange={(e) => setEditCompanyData({...editCompanyData, razon_social: e.target.value})}
                  className="form-input"
                  placeholder="Ej: Vector Solutions S.A."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Nombre Comercial
                </label>
                <Input
                  value={editCompanyData.nombre_comercial}
                  onChange={(e) => setEditCompanyData({...editCompanyData, nombre_comercial: e.target.value})}
                  className="form-input"
                  placeholder="Ej: Vector Solutions"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  CEO / Director
                </label>
                <Input
                  value={editCompanyData.ceo_director}
                  onChange={(e) => setEditCompanyData({...editCompanyData, ceo_director: e.target.value})}
                  className="form-input"
                  placeholder="Ej: Juan Pérez"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Email Principal
                </label>
                <Input
                  type="email"
                  value={editCompanyData.email_principal}
                  onChange={(e) => setEditCompanyData({...editCompanyData, email_principal: e.target.value})}
                  className="form-input"
                  placeholder="contacto@empresa.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Teléfono
                </label>
                <Input
                  value={editCompanyData.telefono}
                  onChange={(e) => setEditCompanyData({...editCompanyData, telefono: e.target.value})}
                  className="form-input"
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Industria
                </label>
                <Input
                  value={editCompanyData.industria}
                  onChange={(e) => setEditCompanyData({...editCompanyData, industria: e.target.value})}
                  className="form-input"
                  placeholder="Ej: Tecnología, Retail, etc."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  País
                </label>
                <Input
                  value={editCompanyData.pais}
                  onChange={(e) => setEditCompanyData({...editCompanyData, pais: e.target.value})}
                  className="form-input"
                  placeholder="Ej: México, Chile, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Ciudad
                </label>
                <Input
                  value={editCompanyData.ciudad}
                  onChange={(e) => setEditCompanyData({...editCompanyData, ciudad: e.target.value})}
                  className="form-input"
                  placeholder="Ej: Ciudad de México"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Número de Empleados
              </label>
              <Input
                type="number"
                value={editCompanyData.empleados}
                onChange={(e) => setEditCompanyData({...editCompanyData, empleados: e.target.value})}
                className="form-input"
                placeholder="Ej: 50"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleUpdateCompanyProfile}
                className="flex-1 btn-gradient"
                disabled={updateCompanyProfileMutation.isLoading}
              >
                {updateCompanyProfileMutation.isLoading ? 'Guardando...' : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Cambios
                  </>
                )}
              </Button>
              <Button
                onClick={() => setShowEditCompany(false)}
                variant="outline"
                className="border-slate-600 text-slate-300"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent 00 Configuration Dialog */}
      <Dialog open={!!showAgent00Config} onOpenChange={() => setShowAgent00Config(null)}>
        <DialogContent className="glass-dark border-blue-500/20 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-400" />
              Configurar Agente 00 - {showAgent00Config?.replace('_', ' ').toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <Card className="bg-blue-900/20 border-blue-500/30">
              <CardContent className="p-4">
                <h4 className="text-sm font-semibold text-blue-300 mb-2">ℹ️ Acerca del Agente 00</h4>
                <p className="text-xs text-slate-300 mb-2">
                  El Agente 00 es responsable de la ingesta centralizada de datos para este módulo. 
                  Recibe archivos (CSV, Excel, PDF), los procesa y distribuye automáticamente a los agentes funcionales, premium y c-level.
                </p>
                <p className="text-xs text-slate-400">
                  <strong>Prompt Interno:</strong> El sistema tiene un prompt interno predefinido que coordina todo el proceso de ingesta y ejecución.
                </p>
              </CardContent>
            </Card>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Prompt Personalizado (Opcional)
              </label>
              <p className="text-xs text-slate-400 mb-3">
                Este prompt se <strong>combina</strong> con el prompt interno del Agente 00. 
                Úsalo para agregar instrucciones específicas para este módulo.
              </p>
              <Textarea
                value={agent00Prompt}
                onChange={(e) => setAgent00Prompt(e.target.value)}
                placeholder="Ejemplo: Enfócate en detectar anomalías en los datos de ventas y prioriza alertas de productos con baja rotación. Al procesar archivos PDF, extrae primero las tablas de resumen ejecutivo..."
                rows={8}
                className="w-full bg-slate-800 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500 mt-2">
                {agent00Prompt.length} caracteres
              </p>
            </div>

            <div className="bg-slate-800/50 p-4 rounded-lg">
              <h4 className="text-sm font-semibold text-slate-300 mb-2">💡 Consejos para el Prompt</h4>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• Especifica prioridades de análisis (ej: "enfócate en tendencias decrecientes")</li>
                <li>• Define alertas críticas (ej: "genera alertas si detectas X")</li>
                <li>• Indica formatos preferenciales (ej: "prioriza datos de los últimos 6 meses")</li>
                <li>• Menciona contexto del negocio (ej: "considera estacionalidad en Q4")</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  saveAgent00ConfigMutation.mutate({
                    moduleType: showAgent00Config,
                    customPrompt: agent00Prompt
                  });
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={saveAgent00ConfigMutation.isPending}
              >
                {saveAgent00ConfigMutation.isPending ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Configuración
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  setShowAgent00Config(null);
                  setAgent00Prompt('');
                }}
                variant="outline"
                className="border-slate-600 text-slate-300"
                disabled={saveAgent00ConfigMutation.isPending}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyConfiguration;